import {uploadActionTypes} from "../constants";
import {ProgressEvent, CompletionEvent, ErrorEvent} from "./useWebSocket";

const initialState = {
  type: uploadActionTypes.START,
  response: null,
  message: "",
  isProcessing: false,
  progressEvent: null as ProgressEvent | null,
  isWebSocketActive: false
};

interface reducerArgs {
  type: uploadActionTypes;
  response: any;
  message: string;
  isProcessing: boolean;
  progressEvent?: ProgressEvent | null;
  isWebSocketActive?: boolean;
}

function uploadReducer(
  state: any,
  {type, response, message, isProcessing, progressEvent, isWebSocketActive}: reducerArgs
) {
  switch (type) {
    case uploadActionTypes.START: {
      return initialState;
    }
    case uploadActionTypes.UPLOADING: {
      return {...state, type: uploadActionTypes.UPLOADING, message, isProcessing};
    }
    case uploadActionTypes.PROCESSING: {
      return {...state, type: uploadActionTypes.PROCESSING, message, isProcessing, isWebSocketActive: true};
    }
    case uploadActionTypes.FETCHING: {
      return {...state, type: uploadActionTypes.FETCHING, message, isProcessing: true, isWebSocketActive: true};
    }
    case uploadActionTypes.ERROR: {
      return {
        ...state,
        type: uploadActionTypes.ERROR,
        response,
        message,
        isProcessing: false,
        isWebSocketActive: false
      };
    }
    case uploadActionTypes.NOT_A_LOG_FILE: {
      return {
        ...state,
        type: uploadActionTypes.NOT_A_LOG_FILE,
        response,
        message,
        isProcessing: false,
        isWebSocketActive: false
      };
    }
    case uploadActionTypes.SUCCESS: {
      return {
        ...state,
        type: uploadActionTypes.SUCCESS,
        response,
        message,
        isProcessing: false,
        isWebSocketActive: false
      };
    }
    case uploadActionTypes.WEBSOCKET_PROGRESS: {
      return {
        ...state,
        progressEvent,
        isWebSocketActive: isWebSocketActive ?? state.isWebSocketActive,
        message: progressEvent?.message ?? state.message
      };
    }
    case uploadActionTypes.WEBSOCKET_COMPLETE: {
      return {
        ...state,
        type: uploadActionTypes.SUCCESS,
        response,
        message: "File processing completed successfully!",
        isProcessing: false,
        isWebSocketActive: false
      };
    }
    case uploadActionTypes.WEBSOCKET_ERROR: {
      return {
        ...state,
        type: uploadActionTypes.ERROR,
        response,
        message,
        isProcessing: false,
        isWebSocketActive: false
      };
    }
    default: {
      throw new Error(`Unhandled type: ${type}`);
    }
  }
}

const start = () => initialState;

const uploadingFile = (message?: string) => ({
  type: uploadActionTypes.UPLOADING,
  response: {},
  message: message || "Uploading file...",
  isProcessing: true
});

const processing = () => ({
  type: uploadActionTypes.PROCESSING,
  response: {},
  message: "Processing file...",
  isProcessing: true
});

const fetchingReport = () => ({
  type: uploadActionTypes.FETCHING,
  response: {},
  message: "Preparing report...",
  isProcessing: true
});

const success = (response: any) => ({
  type: uploadActionTypes.SUCCESS,
  response,
  message: "File processed successfully!",
  isProcessing: false
});

const error = (response: any) => ({
  type: uploadActionTypes.ERROR,
  response,
  message: response?.error || response?.response?.error || "An error occurred processing the file. Please try again.",
  isProcessing: false
});

const badData = (response: any) => ({
  type: uploadActionTypes.NOT_A_LOG_FILE,
  response,
  message: "The file uploaded does not appear to be a game log file.",
  isProcessing: false
});

const webSocketProgress = (progressEvent: ProgressEvent, isWebSocketActive: boolean = true) => ({
  type: uploadActionTypes.WEBSOCKET_PROGRESS,
  response: {},
  message: progressEvent.message,
  isProcessing: true,
  progressEvent,
  isWebSocketActive
});

const webSocketComplete = (response: any) => ({
  type: uploadActionTypes.WEBSOCKET_COMPLETE,
  response,
  message: "File processing completed successfully!",
  isProcessing: false,
  isWebSocketActive: false
});

const webSocketError = (response: any, message: string) => ({
  type: uploadActionTypes.WEBSOCKET_ERROR,
  response,
  message,
  isProcessing: false,
  isWebSocketActive: false
});

const setFile = (fileInfo: any) => ({
  type: uploadActionTypes.START,
  response: fileInfo,
  message: "File selected",
  isProcessing: false
});

export {
  uploadReducer,
  initialState,
  start,
  uploadingFile,
  processing,
  fetchingReport,
  success,
  error,
  badData,
  webSocketProgress,
  webSocketComplete,
  webSocketError,
  setFile
};
